import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { HeroInlineLogo } from "@/components/dataTable";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  formatHours,
  getEmployeeMonthSummary,
  type EmployeeTimeMonthProject,
  type EmployeeTimeMonthSummary,
} from "@/lib/employeeTimes";
import {
  expandEffectiveAbsenceDays,
  listAbsenceRequests,
  type AbsenceDayMarker,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  AbsenceCalendarLegend,
  AbsenceDayBadges,
  HeroAbsenceDayBadges,
} from "@/pages/admin/zeiterfassung/AbsenceCalendarLegend";
import {
  expandHeroAbsenceDays,
  getHeroEmployeeCalendar,
  heroHoursByDayMap,
  sumHeroHoursInMonth,
} from "@/lib/heroEmployeeHr";
import { isWeekend } from "@/pages/admin/zeiterfassung/yearOverviewUtils";
import { CalendarDayBadges } from "@/pages/admin/zeiterfassung/CalendarDayBadge";
import {
  calendarDaysToMap,
  getCalendarDays,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  MONTH_PROJECT_COLUMNS,
  formatMonthProjectLabel,
  formatMonthProjectSubtitle,
  getMonthProjectColumnDefaultWidth,
  getMonthProjectColumnLabel,
} from "@/pages/admin/zeiterfassung/columns";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";
import { useZeiterfassungColumnWidths } from "@/pages/admin/zeiterfassung/useZeiterfassungColumnWidths";
import {
  ZeiterfassungDataTable,
  type ZeiterfassungTableColumn,
} from "@/pages/admin/zeiterfassung/ZeiterfassungDataTable";
import { TimesExportButton } from "@/pages/admin/zeiterfassung/TimesExportButton";

interface MonthOverviewPanelProps {
  refreshKey: number;
  defaultEmployeeLogin?: string;
  defaultEmployeeDisplay?: string;
  allowEmployeePicker?: boolean;
  includeHeroOverlay?: boolean;
}

const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export function MonthOverviewPanel({
  refreshKey,
  defaultEmployeeLogin,
  defaultEmployeeDisplay,
  allowEmployeePicker = true,
  includeHeroOverlay = false,
}: MonthOverviewPanelProps) {
  const now = new Date();
  const [selectedEmployee, setSelectedEmployee] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployeeLogin || "", defaultEmployeeDisplay),
  );
  const employeeLogin = selectedEmployee?.value.login ?? "";
  const employeeDisplay = selectedEmployee?.value.display ?? "";
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState<EmployeeTimeMonthSummary | null>(null);
  const [absences, setAbsences] = useState<EmployeeAbsenceRequest[]>([]);
  const [calendarByDay, setCalendarByDay] = useState<Map<string, EmployeeCalendarDay>>(new Map());
  const [heroHoursByDay, setHeroHoursByDay] = useState<Map<string, number>>(new Map());
  const [heroAbsenceByDay, setHeroAbsenceByDay] = useState<Map<string, AbsenceDayMarker[]>>(new Map());
  const [heroLinked, setHeroLinked] = useState(false);
  const [heroPartnerName, setHeroPartnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!allowEmployeePicker && defaultEmployeeLogin) {
      setSelectedEmployee(
        employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
      );
    }
  }, [allowEmployeePicker, defaultEmployeeLogin, defaultEmployeeDisplay]);

  const { widths, onColumnWidthChange } = useZeiterfassungColumnWidths("month-projects");

  const projectColumns = useMemo(
    (): ZeiterfassungTableColumn<EmployeeTimeMonthProject>[] =>
      MONTH_PROJECT_COLUMNS.map((key) => ({
        key,
        label: getMonthProjectColumnLabel(key),
        defaultWidth: getMonthProjectColumnDefaultWidth(key),
        mono: key === "hours",
        align: key === "hours" || key === "entry_count" ? "right" : "left",
        className: key === "hours" ? "font-semibold" : undefined,
        sortValue: (proj) => {
          if (key === "project") {
            return [formatMonthProjectLabel(proj), formatMonthProjectSubtitle(proj)]
              .filter(Boolean)
              .join(" ");
          }
          if (key === "hours") {
            return Number(proj.app_hours ?? proj.total_hours ?? 0) + Number(proj.hero_hours ?? 0);
          }
          if (key === "entry_count") return proj.entry_count;
          return "";
        },
        render: (proj) => {
          switch (key) {
            case "project":
              return (
                <>
                  <div className="font-medium text-label">{formatMonthProjectLabel(proj) || "—"}</div>
                  {formatMonthProjectSubtitle(proj) && (
                    <div className="text-xs text-label-secondary">
                      {formatMonthProjectSubtitle(proj)}
                    </div>
                  )}
                </>
              );
            case "hours": {
              const appHours = Number(proj.app_hours ?? proj.total_hours ?? 0);
              const heroHours = Number(proj.hero_hours ?? 0);
              if (includeHeroOverlay && heroHours > 0) {
                return (
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">
                      {formatHours(appHours + heroHours)}
                    </div>
                    <div className="text-xs text-label-secondary">
                      App {formatHours(appHours)}
                      {" · "}
                      <span className="inline-flex items-center gap-0.5 align-middle">
                        <HeroInlineLogo />
                        {formatHours(heroHours)}
                      </span>
                    </div>
                  </div>
                );
              }
              return formatHours(appHours);
            }
            case "entry_count":
              return String(proj.entry_count);
            default:
              return null;
          }
        },
      })),
    [includeHeroOverlay],
  );

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!employeeLogin) {
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const heroPromise = includeHeroOverlay
        ? getHeroEmployeeCalendar({ year, user_login: employeeLogin })
        : Promise.resolve(null);
      const [data, absenceRows, calendar, heroData] = await Promise.all([
        getEmployeeMonthSummary({ user_login: employeeLogin, year, month }),
        listAbsenceRequests({ user_login: employeeLogin, year }),
        getCalendarDays({ user_login: employeeLogin, year }),
        heroPromise,
      ]);
      setSummary(data);
      setAbsences(absenceRows);
      setCalendarByDay(calendarDaysToMap(calendar.days));
      if (heroData) {
        setHeroHoursByDay(heroHoursByDayMap(heroData.hours_by_day));
        setHeroAbsenceByDay(expandHeroAbsenceDays(heroData.absences));
        setHeroLinked(heroData.linked);
        setHeroPartnerName(heroData.partner_name);
      } else {
        setHeroHoursByDay(new Map());
        setHeroAbsenceByDay(new Map());
        setHeroLinked(false);
        setHeroPartnerName(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Monatsübersicht konnte nicht geladen werden.");
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
    } finally {
      setLoading(false);
    }
  }, [employeeLogin, year, month, includeHeroOverlay]);

  useRefreshKeyLoad(load, refreshKey);

  const absenceByDay = useMemo(
    () => expandEffectiveAbsenceDays(absences, calendarByDay, { year, month, includePending: true }),
    [absences, calendarByDay, year, month],
  );

  const calendarDays = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = last.getDate();
    const cells: Array<{
      day: number | null;
      hours: number;
      heroHours: number;
      entries: number;
      iso: string | null;
      isWeekend: boolean;
    }> = [];

    for (let i = 0; i < startPad; i++) {
      cells.push({ day: null, hours: 0, heroHours: 0, entries: 0, iso: null, isWeekend: false });
    }
    const byDate = new Map(
      (summary?.days || []).map((d) => [d.work_date.slice(0, 10), d]),
    );
    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const row = byDate.get(iso);
      const heroHours = heroHoursByDay.get(iso) ?? 0;
      // month-summary may already include Hero in day total_hours; subtract when
      // the Hero overlay is shown so hours are not counted twice.
      const combinedHours = Number(row?.total_hours || 0);
      const appHours = heroHours > 0 ? Math.max(0, combinedHours - heroHours) : combinedHours;
      cells.push({
        day,
        hours: appHours,
        heroHours,
        entries: Number(row?.entry_count || 0),
        iso,
        isWeekend: isWeekend(year, month, day),
      });
    }
    return cells;
  }, [summary, year, month, heroHoursByDay]);

  const heroMonthHours = useMemo(
    () => (includeHeroOverlay ? sumHeroHoursInMonth(heroHoursByDay, year, month) : 0),
    [includeHeroOverlay, heroHoursByDay, year, month],
  );

  const appMonthHours = Number(summary?.total_hours ?? 0);
  const istMonthHours = appMonthHours + (includeHeroOverlay ? heroMonthHours : 0);
  // Wie Jahresübersicht: App-Soll als Primärwert (Überstundenrechnung), Hero nur Hinweis.
  const appSollMonthHours = Number(summary?.app_expected_hours ?? summary?.expected_hours ?? 0);
  const heroSollMonthHours =
    summary?.hero_expected_hours != null ? Number(summary.hero_expected_hours) : null;
  const sollMonthHours = appSollMonthHours;
  const monthHoursDelta = istMonthHours - sollMonthHours;
  const hasSoll = summary?.has_soll !== false;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios md:grid-cols-4">
        {allowEmployeePicker ? (
          <EmployeeLockedAutocomplete
            value={selectedEmployee}
            onChange={setSelectedEmployee}
          />
        ) : (
          <label className="space-y-1">
            <span className="eh-label">Mitarbeiter</span>
            <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
              {employeeDisplay || employeeLogin}
            </div>
          </label>
        )}
        <label className="space-y-1">
          <span className="eh-label">Jahr</span>
          <select
            className="eh-input w-full"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="eh-label">Monat</span>
          <select
            className="eh-input w-full"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={name} value={idx + 1}>{name}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={!employeeLogin}>
            Laden
          </Button>
          <TimesExportButton
            employeeLogin={employeeLogin}
            year={year}
            month={month}
            includeHero={includeHeroOverlay}
            disabled={!employeeLogin}
          />
        </div>
      </div>

      {!employeeLogin && allowEmployeePicker && (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center text-sm text-label-secondary">
          Bitte einen Mitarbeiter auswählen, um die Monatsübersicht anzuzeigen.
        </div>
      )}

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {employeeLogin && loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {employeeLogin && !loading && summary && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-ios border border-separator bg-card p-4 shadow-ios md:col-span-2">
              <div className="mb-3 text-sm text-label-secondary">
                {MONTH_NAMES[month - 1]} {year}
                {includeHeroOverlay && heroLinked && heroPartnerName
                  ? ` · Hero: ${heroPartnerName}`
                  : includeHeroOverlay && !heroLinked
                    ? " · Hero nicht verknüpft"
                    : ""}
              </div>
              <div className={clsx("grid grid-cols-1 gap-4", hasSoll && "sm:grid-cols-2")}>
                {hasSoll && (
                  <div className="rounded-lg border border-separator bg-fill-tertiary/30 px-4 py-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                      Soll
                    </div>
                    <div className="mt-1 text-3xl font-bold tabular-nums text-label">
                      {formatHours(sollMonthHours)}
                    </div>
                    {heroSollMonthHours != null && heroSollMonthHours > 0 ? (
                      <div className="mt-1 text-sm text-label-secondary">
                        App (Modell) · Hero {formatHours(heroSollMonthHours)}
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-label-secondary">
                        laut Arbeitszeitmodell (abzgl. FT/Abw.)
                      </div>
                    )}
                  </div>
                )}
                <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Ist
                  </div>
                  <div className="mt-1 text-3xl font-bold tabular-nums text-accent">
                    {formatHours(istMonthHours)}
                  </div>
                  <div className="mt-1 text-sm text-label-secondary">
                    App {formatHours(appMonthHours)}
                    {includeHeroOverlay && heroMonthHours > 0 && (
                      <>
                        {" · "}
                        <span className="inline-flex items-center gap-0.5 align-middle">
                          <HeroInlineLogo />
                          {formatHours(heroMonthHours)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {hasSoll && sollMonthHours > 0 && (
                <div className="mt-3 border-t border-separator pt-3 text-sm text-label-secondary">
                  Differenz (Ist − App-Soll):{" "}
                  <span
                    className={clsx(
                      "font-semibold tabular-nums",
                      monthHoursDelta >= 0 ? "text-label" : "text-red-600 dark:text-red-400",
                    )}
                  >
                    {formatHours(monthHoursDelta)}
                  </span>
                </div>
              )}
            </div>
            <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <div className="text-xs text-label-secondary">Arbeitstage</div>
              <div className="mt-1 text-2xl font-semibold text-label">{summary.days.length}</div>
              <div className="mt-1 text-sm text-label-secondary">mit App-Einträgen</div>
            </div>
            <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <div className="text-xs text-label-secondary">Projekte</div>
              <div className="mt-1 text-2xl font-semibold text-label">{summary.by_project.length}</div>
            </div>
            {hasSoll && summary.overtime_balance_hours != null && (
              <div className="rounded-ios border border-separator bg-card p-4 shadow-ios md:col-span-3">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-xs text-label-secondary">Überstundenstand</div>
                    <div className="mt-1 text-2xl font-semibold text-label">
                      {formatHours(summary.overtime_balance_hours)}
                    </div>
                    <div className="mt-1 text-xs text-label-tertiary">
                      Heutiger Tag nicht berücksichtigt
                    </div>
                    {summary.opening_effective_date && (
                      <div className="mt-1 text-sm text-label-secondary">
                        Startstand {formatHours(summary.opening_balance_hours ?? 0)} ab{" "}
                        {new Date(`${summary.opening_effective_date.slice(0, 10)}T12:00:00`).toLocaleDateString(
                          "de-DE",
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
            <h3 className="mb-3 text-sm font-semibold text-label">Kalender</h3>
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-label-secondary">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d, idx) => (
                <div
                  key={d}
                  className={clsx("py-1", idx >= 5 && "text-label-tertiary font-bold")}
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((cell, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    cell.day
                      ? "min-h-[92px] rounded-lg border border-separator p-2 text-left"
                      : "min-h-[92px]",
                    cell.day && cell.isWeekend && "bg-fill-secondary/70",
                    cell.day && !cell.isWeekend && "bg-fill-tertiary",
                  )}
                >
                  {cell.day && (
                    <>
                      <div className="text-xs font-semibold text-label">{cell.day}</div>
                      {cell.hours > 0 && (
                        <div className="mt-1 text-xs font-medium text-accent">
                          {formatHours(cell.hours)}
                        </div>
                      )}
                      {cell.heroHours > 0 &&
                        !(heroAbsenceByDay.get(cell.iso!)?.length) && (
                        <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                          <HeroInlineLogo />
                          {formatHours(cell.heroHours)}
                        </div>
                      )}
                      {cell.entries > 0 && (
                        <div className="text-[10px] text-label-secondary">
                          {cell.entries} Eintr.
                        </div>
                      )}
                      {cell.iso && (
                        <>
                          <AbsenceDayBadges markers={absenceByDay.get(cell.iso) ?? []} />
                          <HeroAbsenceDayBadges markers={heroAbsenceByDay.get(cell.iso) ?? []} />
                          {calendarByDay.get(cell.iso) && (
                            <CalendarDayBadges days={[calendarByDay.get(cell.iso)!]} />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <AbsenceCalendarLegend showHeroLegend={includeHeroOverlay && heroLinked} />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-label">Nach Projekt</h3>
            <ZeiterfassungDataTable
              rows={summary.by_project}
              columns={projectColumns}
              rowKey={(row) => row.auftrag_id}
              widths={widths}
              onColumnWidthChange={onColumnWidthChange}
              emptyText="Keine Projektstunden in diesem Monat."
              renderMobileCard={(proj) => {
                const appHours = Number(proj.app_hours ?? proj.total_hours ?? 0);
                const heroHours = Number(proj.hero_hours ?? 0);
                return (
                <div className="flex items-center justify-between gap-3 rounded-ios border border-separator bg-card px-4 py-3 shadow-ios">
                  <div>
                    <div className="font-medium text-label">{formatMonthProjectLabel(proj) || "—"}</div>
                    {formatMonthProjectSubtitle(proj) && (
                      <div className="text-xs text-label-secondary">
                        {formatMonthProjectSubtitle(proj)}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums text-label">
                      {formatHours(includeHeroOverlay && heroHours > 0 ? appHours + heroHours : appHours)}
                    </div>
                    {includeHeroOverlay && heroHours > 0 ? (
                      <div className="text-xs text-label-secondary">
                        App {formatHours(appHours)}
                        {" · "}
                        <span className="inline-flex items-center gap-0.5 align-middle">
                          <HeroInlineLogo />
                          {formatHours(heroHours)}
                        </span>
                      </div>
                    ) : (
                      <div className="text-xs text-label-secondary">
                        {proj.entry_count} Einträge
                      </div>
                    )}
                  </div>
                </div>
                );
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
